import { useState, useCallback, useEffect, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { resetAllReduxStates } from '../../redux/reducer/resetAllReduxStatesAction'
import { logoutService } from '../api/authenticationServices'
import { getUserProfile } from '../api/userServices'
import { TOAST } from '../constants/constant'
import { TOASTIFY_MESSAGES } from '../constants/messages'
import {
  getUserCookie,
  removeUserCookie,
  setUserCookie,
} from '../helperFunctions/cookieToken'
import { endAuthSession } from '../api/axiosInstance'
import { routes } from '../../config/routes'
import { toastNotify } from '../helperFunctions/toastNotify'

export const useAuth = () => {
  const [token, setToken] = useState('')
  const [tokenExpiry, setTokenExpiry] = useState('')
  const [userRole, setUserRole] = useState('')
  const [userId, setUserId] = useState('')
  // true while fetching role from backend on page refresh; prevents premature route rendering
  const [roleLoading, setRoleLoading] = useState(true)
  const history = useHistory()
  const dispatch = useDispatch()

  // 🔒 Prevent multiple logout calls. Stays set until the next login() so a
  // logged-out app can never run the logout sequence a second time.
  const isLoggingOut = useRef(false)

  const endSession = useCallback(
    (notice) => {
      // 🚨 Prevent multiple calls
      if (isLoggingOut.current) return
      isLoggingOut.current = true

      // Requests still in flight belong to the session being ended -- their
      // 401s must not re-trigger logout or reach a session started later.
      endAuthSession()

      // Revoke the session server-side without blocking the local cleanup
      // below -- this also runs for the global 401 listener and the
      // exact-time expiry timer, which must leave the browser logged out even
      // if the backend is unreachable. The HttpOnly auth cookie may remain
      // valid server-side until it naturally expires in that case, so the
      // failure is surfaced via a toast instead of being swallowed.
      logoutService().catch(() => {
        toastNotify(TOAST.error, TOASTIFY_MESSAGES.logoutSyncFailedMessage)
      })

      // Remove cookie first so every render from here on (ProtectedRoute,
      // AppLogout) already sees the session as ended
      removeUserCookie()

      // Clear all auth-related and session data from localStorage
      localStorage.removeItem('currentConfigurationId')
      localStorage.removeItem('isAssociatedList')
      localStorage.removeItem('selectedTab')
      localStorage.removeItem('projectData')
      // Legacy keys (kept for safety in case of stale browsers)
      localStorage.removeItem('auth_token')
      localStorage.removeItem('User_Id')

      // Redirect before resetting state, so the protected page unmounts
      // instead of re-rendering with reset/empty data.
      // replace prevents back-navigation to the authenticated page.
      history.replace(routes.loginpage.path)

      // Clear React state
      setToken(null)
      setTokenExpiry(null)
      setUserRole(null)
      setUserId(null)
      setRoleLoading(false)

      // Clear redux store
      dispatch(resetAllReduxStates())

      if (notice) {
        toastNotify(TOAST.info, notice)
      }
    },
    [dispatch, history]
  )

  // User-initiated logout. Ignores its arguments so it can be passed straight
  // to an onClick without the event being treated as a notice.
  const logout = useCallback(() => endSession(), [endSession])

  // Logout forced by the session ending (401 or tokenExpiry reached)
  const expireSession = useCallback(
    () => endSession(TOASTIFY_MESSAGES.sessionExpiredMessage),
    [endSession]
  )

  // GLOBAL 401 LISTENER
  useEffect(() => {
    window.addEventListener('auth:unauthorized', expireSession)

    return () => {
      window.removeEventListener('auth:unauthorized', expireSession)
    }
  }, [expireSession])

  // EXACT-TIME SESSION EXPIRY
  // ProtectedRoute and AuthVerify only re-check tokenExpiry when something
  // else happens (a render, a navigation). A user sitting idle on a single
  // page never triggers either, so an expired session would otherwise go
  // undetected until the next route change or API call. Schedule logout
  // to fire at the exact expiry moment instead.
  useEffect(() => {
    if (!tokenExpiry) return undefined

    const delay = new Date(tokenExpiry).getTime() - Date.now()

    if (delay <= 0) {
      expireSession()
      return undefined
    }

    const timerId = setTimeout(expireSession, delay)
    return () => clearTimeout(timerId)
  }, [tokenExpiry, expireSession])

  // Fetches the authoritative role from the backend. Called on page refresh so
  // the role is always server-sourced and cannot be influenced by cookie tampering.
  const fetchAndSetRole = useCallback(
    async (uid) => {
      if (!uid) {
        setRoleLoading(false)
        return
      }
      try {
        const response = await getUserProfile(uid)
        const role = response?.data?.data?.role
        if (role) {
          setUserRole(role)
        } else {
          // Unexpected response (session likely expired) — force logout for safety
          logout()
        }
      } catch {
        logout()
      } finally {
        setRoleLoading(false)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [logout]
  )

  useEffect(() => {
    const currentUserData = getUserCookie()
    const storedUserId = currentUserData?.userId

    // Detect stale cookie written by the old encrypted format: userId was an RSA
    // ciphertext blob, not a numeric string. Clear it so the user gets a clean
    // login prompt instead of seeing broken API calls with a garbled user ID.
    if (storedUserId && isNaN(Number(storedUserId))) {
      removeUserCookie()
      setRoleLoading(false)
      return
    }

    const uid = storedUserId ?? ''
    setUserId(uid)
    setTokenExpiry(currentUserData?.tokenExpiry ?? '')

    if (uid) {
      // Role is intentionally NOT read from cookies — always sourced from API
      fetchAndSetRole(uid)
    } else {
      setRoleLoading(false)
    }
  }, [fetchAndSetRole])

  const login = useCallback((userData) => {
    const { userId, authToken, authTokenExpiry, role, isUserLoggingIn } =
      userData
    setToken(authToken)
    setUserId(userId)
    // Role comes directly from the login API response (backend-authoritative)
    setUserRole(role)
    setTokenExpiry(authTokenExpiry)
    setRoleLoading(false)
    isLoggingOut.current = false

    if (isUserLoggingIn) {
      // userRole is deliberately NOT stored in the cookie to prevent
      // client-side role escalation via cookie tampering
      setUserCookie({
        userId: userId.toString(),
        tokenExpiry: authTokenExpiry,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return {
    login,
    logout,
    token,
    tokenExpiry,
    userId,
    userRole,
    roleLoading,
  }
}
